import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FileText, Fullscreen, X } from "lucide-react";

interface FullscreenViewDialogProps {
  mediaUrl: string;
  isVideo?: boolean;
  isPdf?: boolean;
}

export default function FullscreenViewDialog({ mediaUrl, isVideo = false, isPdf = false }: FullscreenViewDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [mediaError, setMediaError] = useState(false);

  const handleMediaError = () => {
    console.error("Failed to load media in fullscreen view");
    setMediaError(true);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <button className="flex items-center p-1 py-0 text-sm">
          <Fullscreen size={20} />
          <span className="ml-1">View</span>
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-[calc(80%)] rounded-sm w-screen h-[85vh] p-2 pt-4">
        <div className="relative w-full h-full">
          <DialogTitle className="p-3">
            {isPdf ? "View PDF" : isVideo ? "View Video" : "View Image"}
          </DialogTitle>
          {isVideo ? (
            <video
              src={mediaUrl}
              controls
              className="w-full h-full object-contain"
              onError={handleMediaError}
            />
          ) : isPdf ? (
            mediaError ? (
              <div className="w-full h-[calc(100%-60px)] flex items-center justify-center bg-gray-100">
                <div className="text-red-500 text-center">
                  <div className="text-5xl mb-2">⚠️</div>
                  <div>Failed to load PDF</div>
                </div>
              </div>
            ) : (
              <div className="w-full h-[calc(100%-60px)] flex flex-col items-center justify-center">
                <iframe
                  src={`${mediaUrl}#toolbar=1&navpanes=1`}
                  className="w-full h-full border-none"
                  onError={handleMediaError}
                  title="PDF Viewer"
                />
                <div className="text-sm text-gray-500 mt-2">
                  <p>If the PDF doesn't load properly, try downloading it instead.</p>
                </div>
              </div>
            )
          ) : mediaError ? (
            <div className="w-full h-[calc(100%-60px)] flex items-center justify-center bg-gray-100">
              <div className="text-red-500 text-center">
                <div className="text-5xl mb-2">⚠️</div>
                <div>Failed to load image</div>
              </div>
            </div>
          ) : (
            <img
              src={mediaUrl}
              alt="Fullscreen Image"
              className="shadow-md w-[80%] h-[80%] object-contain"
              onError={handleMediaError}
            />
          )}
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-0 right-0 z-50 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            aria-label="Close fullscreen view"
          >
            <X size={24} />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}